/**
 * ChildAttackPathsTable — attack paths inherited from child TARAs
 * (hierarchical mode). Read-only data + Relevant checkbox +
 * Justification input. Attack-vector FilterDrawer also applies here.
 *
 * Mirror of step4-threats/ChildThreatsTable.tsx.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import { getFeasibilityColor } from '../../../../utils/severityStyle';
import {
  ATTACK_VECTORS,
  type AttackPathRecord,
} from './attackPathFieldSchema';
import styles from './ChildAttackPathsTable.module.css';
import tableStyles from './AttackPathTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const renderFeasibility = (row: ChildAttackPathView) =>
  row.attack_feasibility_rating ? (
    <LevelBadge
      color={getFeasibilityColor(row.attack_feasibility_rating)}
      label={String(row.attack_feasibility_rating)}
      size="sm"
    />
  ) : (
    ''
  );

const renderVector = (row: ChildAttackPathView) =>
  row.attack_vector ? <CategoryPill label={String(row.attack_vector)} size="sm" /> : '';

export interface ChildAttackPathView extends AttackPathRecord {}

export interface ChildAttackPathsTableProps {
  paths: ChildAttackPathView[];
  onToggleRelevance: (attackPathId: string) => void;
  onJustificationChange: (attackPathId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildAttackPathsTable({
  paths,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildAttackPathsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [vectorValues, setVectorValues] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const vectorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of ATTACK_VECTORS) counts[v.value] = 0;
    for (const p of paths) {
      const k = String(p.attack_vector ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [paths]);

  const vectorFiltered = useMemo(
    () => (vectorValues.length === 0
      ? paths
      : paths.filter((p) => vectorValues.includes(String(p.attack_vector ?? '')))),
    [paths, vectorValues],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vectorFiltered;
    return vectorFiltered.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [vectorFiltered, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.attack_path_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'attack_path_id', label: 'ID', sortable: true, width: '7%' },
      { key: 'threat_id', label: 'Threat', sortable: true, width: '8%' },
      { key: 'asset_id', label: 'Asset', sortable: true, width: '8%' },
      { key: 'attack_vector', label: 'Vector', sortable: true, width: '9%', render: renderVector },
      { key: 'attack_path', label: 'Attack Path', sortable: false, width: '28%' },
      {
        key: 'attack_feasibility_rating',
        label: 'Feasibility',
        sortable: true,
        width: '11%',
        render: renderFeasibility,
      },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '10%',
        render: (row: ChildAttackPathView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.attack_path_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.attack_path_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '19%',
        render: (row: ChildAttackPathView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.attack_path_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  const vectorFilter = {
    key: 'attack_vector',
    label: 'Vector',
    values: vectorValues,
    onChange: (next: string[]) => {
      setVectorValues(next);
      setPage(1);
    },
    options: ATTACK_VECTORS.map((v) => ({
      value: v.value,
      label: v.label,
      count: vectorCounts[v.value] ?? 0,
    })),
  };

  return (
    <div className={tableStyles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[vectorFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        emptyMessage="No inherited attack paths from child TARAs."
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search inherited attack paths…',
            }}
            count={{ value: filtered.length, label: 'Inherited Paths' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || vectorValues.length > 0}
            actions={
              <RelevanceEditToggle
                editing={liveEditing}
                onToggle={handleToggle}
                pending={saving}
                disabled={disabled}
              />
            }
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
